import React, { useState, useEffect } from 'react';
import {
  X, Plus, Trash2, DollarSign, Calendar, FileText,
  AlertTriangle, Hash, MapPin, Users, Receipt, Banknote,
  TrendingDown, CheckCircle, Clock, CreditCard, Phone, ChevronDown
} from 'lucide-react';
import { formatCurrency, safeAmount, formatDate } from '../../utils/helpers';
import { leadsService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { DatePicker, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';

export default function PaymentDetailModal({ lead, onClose, onUpdate }) {
  const { t } = useLanguage();
  const [amount, setAmount]         = useState('');
  const [date, setDate]             = useState(new Date().toISOString().split('T')[0]);
  const [type, setType]             = useState('Mid-payment');
  const [notes, setNotes]           = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const isExternalApi = lead?.isExternalApi;

  /* ── payment list ─────────────────────────────────────────────────── */
  const initPayments = () => {
    if (isExternalApi) {
      return Array.isArray(lead?.payment_transactions)
        ? lead.payment_transactions.map(p => ({
            id: p.id,
            amount: p.receipt_amount,
            date: p.payment_date,
            type: p.service_name || 'Payment',
            notes: p.description,
            rvNo: p.rv_no || null,
            slipNo: p.slip_no || null,
            isLegacy: true,
            isExternal: true,
          }))
        : [];
    }
    const cur = Array.isArray(lead?.payments) ? lead.payments : [];
    const hasAdv = cur.some(p =>
      p.type === 'Advance' || p.type === 'Advance (Initial)' ||
      (p.notes && p.notes.toLowerCase().includes('initial booking'))
    );
    if (lead?.advanceAmount > 0 && !hasAdv) {
      return [{
        id: 'legacy-advance',
        amount: lead.advanceAmount,
        date: lead.bookedAt || lead.createdAt || new Date().toISOString(),
        type: 'Advance (Initial)',
        notes: 'Initial booking payment',
        isLegacy: true,
      }, ...cur];
    }
    return cur;
  };

  const [payments, setPayments] = useState(initPayments);
  useEffect(() => { setPayments(initPayments()); }, [lead]);

  /* ── financials ───────────────────────────────────────────────────── */
  const fs         = lead?.financial_summary || {};
  const totalValue = isExternalApi
    ? safeAmount(fs.invoice_net_amount)
    : safeAmount(lead?.finalAmount || lead?.amount);
  const totalPaid  = isExternalApi
    ? safeAmount(fs.total_received_amount)
    : payments.reduce((s, p) => s + safeAmount(p.amount), 0);
  const remaining  = Math.round(totalValue - totalPaid);
  const pct        = totalValue > 0 ? Math.min(100, Math.round((totalPaid / totalValue) * 100)) : 0;

  /* ── payment status badge ─────────────────────────────────────────── */
  const statusBadge = () => {
    if (pct >= 100) return { label: 'Paid', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: CheckCircle };
    if (pct > 0)    return { label: 'Partial', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Clock };
    return { label: 'Pending', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Clock };
  };
  const { label: statusLabel, cls: statusCls, Icon: StatusIcon } = statusBadge();

  /* ── add payment ──────────────────────────────────────────────────── */
  const handleAddPayment = async () => {
    if (!amount || !date) return;
    const num = safeAmount(amount);
    if (num > remaining + 1) {
      setLocalError(`Cannot exceed remaining balance (${formatCurrency(remaining)})`);
      return;
    }
    setLocalError(null);
    setIsSubmitting(true);
    try {
      const pd = { amount: num, date: new Date(date).toISOString(), type, notes, shift: lead?.shift || 'Day' };
      const res = await leadsService.addPayment(lead.id, pd);
      if (res.status) {
        const updated = [...payments, res.data];
        const newPaid = totalPaid + num;
        const newDue  = Math.round(totalValue - newPaid);
        setPayments(updated);
        onUpdate?.(lead.id, { payments: updated, totalPaid: newPaid, totalDue: newDue, ...(type.includes('Advance') ? { advanceAmount: num } : {}) });
        await leadsService.addNote(lead.id, { type: 'PAYMENT_ADDED', content: JSON.stringify({ amount: num, paymentType: type, remaining: newDue }) });
        if (newDue <= 0) await leadsService.addNote(lead.id, { type: 'PAYMENT_COMPLETED', content: JSON.stringify({ finalAmount: totalValue, totalPaid: newPaid }) });
        setAmount(''); setNotes('');
      }
    } catch { setLocalError('Failed to add payment. Please try again.'); }
    finally { setIsSubmitting(false); }
  };

  /* ── delete payment ───────────────────────────────────────────────── */
  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const p = payments.find(x => x.id === deleteConfirm);
      if (!p) return;
      if (deleteConfirm === 'legacy-advance') await leadsService.update(lead.id, { advanceAmount: 0 });
      else await leadsService.deletePayment(lead.id, deleteConfirm);
      const updated  = payments.filter(x => x.id !== deleteConfirm);
      const newPaid  = updated.reduce((s, x) => s + safeAmount(x.amount), 0);
      setPayments(updated);
      onUpdate?.(lead.id, { payments: updated, totalPaid: newPaid, totalDue: totalValue - newPaid, ...(deleteConfirm === 'legacy-advance' ? { advanceAmount: 0 } : {}) });
      await leadsService.addNote(lead.id, { type: 'PAYMENT_DELETED', content: JSON.stringify({ amount: p.amount, paymentType: p.type }) });
    } catch { setLocalError('Failed to delete payment.'); }
    finally { setDeleteConfirm(null); }
  };

  const typeLabel = (tp) => {
    if (isExternalApi) return tp;
    return { 'Advance': t('advanceInitial'), 'Advance (Initial)': t('advanceInitial'), 'Mid-payment': t('midPayment'), 'Final': t('finalPayment'), 'Custom': t('custom') }[tp] || tp;
  };

  const txnColor = (tp) => {
    const s = (tp || '').toLowerCase();
    if (s.includes('advance')) return 'bg-purple-100 text-purple-600';
    if (s.includes('final'))   return 'bg-emerald-100 text-emerald-600';
    return 'bg-blue-100 text-blue-600';
  };

  /* ── render ───────────────────────────────────────────────────────── */
  return (
    /* Backdrop — matches LeadDetailModal: slate-900/60 + backdrop-blur-sm */
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Sheet on mobile (slides from bottom), centered card on sm+ */}
      <div className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh] animate-scale-in overflow-hidden">

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-start justify-between gap-3 sticky top-0 z-10">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base sm:text-lg font-bold text-gray-900">{t('paymentHistory')}</h2>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusCls}`}>
                <StatusIcon className="w-3 h-3" />{statusLabel}
              </span>
            </div>
            {/* Client info row */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
              <span className="font-semibold text-gray-800 text-sm truncate">{lead?.clientName}</span>
              {lead?.phone && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Phone className="w-3 h-3" />{lead.phone}
                </span>
              )}
              {lead?.orderNo && (
                <span className="flex items-center gap-1 text-xs font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                  <Hash className="w-3 h-3" />{lead.orderNo}
                </span>
              )}
            </div>
            {/* Event meta row */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              {lead?.eventType && <span className="text-xs text-gray-500">{lead.eventType}</span>}
              {lead?.venue && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <MapPin className="w-3 h-3" />{lead.venue}
                </span>
              )}
              {lead?.shift && (
                <span className="text-xs text-gray-500 capitalize">{lead.shift} Shift</span>
              )}
              {lead?.no_of_guest && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Users className="w-3 h-3" />{lead.no_of_guest} guests
                </span>
              )}
              {lead?.eventDate && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  {formatDate(lead.eventDate, { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full shrink-0 transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto bg-slate-50">

          {/* Inline banners */}
          {deleteConfirm && (
            <div className="mx-4 sm:mx-6 mt-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-red-700 text-sm font-medium">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                Delete this payment?
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setDeleteConfirm(null)} className="px-3 py-1.5 text-xs rounded-lg bg-gray-100 hover:bg-gray-200 font-medium">Cancel</button>
                <button onClick={confirmDelete} className="px-3 py-1.5 text-xs rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium">Delete</button>
              </div>
            </div>
          )}
          {localError && (
            <div className="mx-4 sm:mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span className="flex-1">{localError}</span>
              <button onClick={() => setLocalError(null)} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
            </div>
          )}

          <div className="p-4 sm:p-6 space-y-5">

            {/* ── Financial summary cards ──────────────────────────── */}
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4">
                <p className="text-[10px] sm:text-xs text-gray-400 font-semibold uppercase tracking-wide">
                  {isExternalApi ? 'Invoice Net' : t('totalValue')}
                </p>
                <p className="text-base sm:text-xl font-bold text-gray-900 mt-1 truncate">{formatCurrency(totalValue)}</p>
                {isExternalApi && safeAmount(fs.invoice_discount_amount) > 0 && (
                  <p className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-0.5">
                    <TrendingDown className="w-3 h-3" />-{formatCurrency(safeAmount(fs.invoice_discount_amount))}
                  </p>
                )}
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4">
                <p className="text-[10px] sm:text-xs text-gray-400 font-semibold uppercase tracking-wide">{t('collected')}</p>
                <p className={`text-base sm:text-xl font-bold mt-1 truncate ${pct >= 100 ? 'text-emerald-600' : 'text-blue-600'}`}>
                  {formatCurrency(totalPaid)}
                </p>
                {isExternalApi && safeAmount(fs.cash_received_amount) > 0 && (
                  <p className="text-[10px] text-gray-400 mt-0.5">Cash: {formatCurrency(safeAmount(fs.cash_received_amount))}</p>
                )}
              </div>
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 sm:p-4">
                <p className="text-[10px] sm:text-xs text-gray-400 font-semibold uppercase tracking-wide">{t('remaining')}</p>
                <p className={`text-base sm:text-xl font-bold mt-1 truncate ${remaining > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {formatCurrency(Math.max(0, isExternalApi ? safeAmount(fs.balance_amount ?? remaining) : remaining))}
                </p>
              </div>
            </div>

            {/* Collection progress bar */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-semibold text-gray-600">Collection Progress</span>
                <span className="text-xs font-bold text-gray-800">{pct}%</span>
              </div>
              <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-blue-500' : pct >= 30 ? 'bg-amber-500' : 'bg-red-400'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              {isExternalApi && (
                <div className="flex justify-between mt-2 text-[10px] text-gray-400">
                  <span>Gross: {formatCurrency(safeAmount(fs.invoice_gross_amount))}</span>
                  {safeAmount(fs.invoice_discount_amount) > 0 && <span>Disc: -{formatCurrency(safeAmount(fs.invoice_discount_amount))}</span>}
                  <span>Balance: {formatCurrency(safeAmount(fs.balance_amount ?? remaining))}</span>
                </div>
              )}
            </div>

            {/* ── Booking note ─────────────────────────────────────── */}
            {(() => {
              const bn = lead?.bookingNotes || lead?.booking_notes;
              return bn && bn !== '-' ? (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <FileText className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-amber-800 mb-1">Booking Note</p>
                    <p className="text-sm text-amber-700 whitespace-pre-wrap">{bn}</p>
                  </div>
                </div>
              ) : null;
            })()}

            {/* ── Invoice breakdown (external only) ────────────────── */}
            {isExternalApi && Array.isArray(lead?.invoice_items) && lead.invoice_items.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-blue-500" />
                    <h3 className="font-bold text-gray-800 text-sm">Invoice Breakdown</h3>
                  </div>
                  <span className="text-xs text-gray-400 hidden sm:block">
                    Gross {formatCurrency(safeAmount(fs.invoice_gross_amount))}
                    {safeAmount(fs.invoice_discount_amount) > 0 && ` · Disc -${formatCurrency(safeAmount(fs.invoice_discount_amount))}`}
                  </span>
                </div>
                <div className="divide-y divide-gray-100">
                  {lead.invoice_items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-800 text-sm">{item.service_name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Qty: {item.booked_qty}
                          {item.lumpsum > 0 && ` · ${formatCurrency(item.lumpsum)}`}
                          {item.rate > 0 && ` · ${formatCurrency(item.rate)}/pax`}
                        </p>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className="font-bold text-gray-900 text-sm">{formatCurrency(item.sub_total)}</p>
                        {item.discount > 0 && (
                          <p className="text-xs text-emerald-600">-{formatCurrency(item.discount)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Net total row */}
                  <div className="flex items-center justify-between px-4 py-3 bg-blue-50">
                    <p className="font-bold text-blue-800 text-sm">Net Total</p>
                    <p className="font-bold text-blue-800 text-sm">{formatCurrency(safeAmount(fs.invoice_net_amount))}</p>
                  </div>
                </div>
              </div>
            )}

            {/* ── Add payment form (CRM only) ───────────────────────── */}
            {!isExternalApi && (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                <h3 className="font-bold text-gray-800 text-sm mb-4 flex items-center gap-2">
                  <Plus className="w-4 h-4 text-blue-500" />{t('addPayment')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{t('amount')}</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">Rs</span>
                      <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        placeholder="0" />
                    </div>
                    <p className="mt-1 text-xs text-gray-400">
                      After: <span className={`font-semibold ${safeAmount(amount) > remaining + 1 ? 'text-red-500' : 'text-gray-700'}`}>
                        {formatCurrency(Math.max(0, remaining - safeAmount(amount)))}
                      </span>
                    </p>
                  </div>
                  <div>
                    <DatePicker 
                      label={t('date')} 
                      value={date} 
                      onChange={e => setDate(e.target.value)} 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{t('type')}</label>
                    <Dropdown>
                      <DropdownTrigger>
                        <button
                          type="button"
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white text-left flex justify-between items-center"
                        >
                          {
                            type === 'Advance' ? t('advanceInitial') :
                            type === 'Advance (Initial)' ? t('advanceInitial') :
                            type === 'Mid-payment' ? t('midPayment') :
                            type === 'Final' ? t('finalPayment') :
                            type === 'Custom' ? t('custom') : type
                          }
                          <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
                        </button>
                      </DropdownTrigger>
                      <DropdownPopover className="w-full max-h-60 overflow-y-auto">
                        <DropdownMenu
                          selectedKeys={new Set([type])}
                          selectionMode="single"
                          onSelectionChange={(keys) => setType(Array.from(keys)[0])}
                        >
                          <DropdownItem id="Advance">{t('advanceInitial')}</DropdownItem>
                          <DropdownItem id="Mid-payment">{t('midPayment')}</DropdownItem>
                          <DropdownItem id="Final">{t('finalPayment')}</DropdownItem>
                          <DropdownItem id="Custom">{t('custom')}</DropdownItem>
                        </DropdownMenu>
                      </DropdownPopover>
                    </Dropdown>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{t('notesOptional')}</label>
                    <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="e.g. Bank Transfer ID..." />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button onClick={handleAddPayment}
                    disabled={isSubmitting || !amount || safeAmount(amount) > remaining + 1 || remaining < 1}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                    {isSubmitting ? t('adding') : t('addPayment')}
                  </button>
                </div>
              </div>
            )}

            {/* ── Payment transactions ──────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-blue-500" />
                  {isExternalApi ? 'Payment Transactions' : t('paymentHistory')}
                </h3>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                  {payments.length} record{payments.length !== 1 ? 's' : ''}
                </span>
              </div>

              {payments.length === 0 ? (
                <div className="text-center py-10 text-gray-400 bg-white rounded-xl border border-dashed border-gray-200 text-sm">
                  {t('noPaymentsRecorded')}
                </div>
              ) : (
                <div className="space-y-2">
                  {[...payments].sort((a, b) => new Date(b.date) - new Date(a.date)).map(p => (
                    <div key={p.id}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-between p-3 sm:p-4 group hover:border-blue-200 transition-colors">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 ${txnColor(p.type)}`}>
                          <DollarSign className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 text-sm">{typeLabel(p.type)}</p>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <Calendar className="w-3 h-3" />
                              {formatDate(p.date, { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            {p.rvNo && (
                              <span className="text-xs font-mono text-blue-500 flex items-center gap-0.5">
                                <Hash className="w-3 h-3" />{p.rvNo}
                              </span>
                            )}
                            {p.slipNo && p.slipNo !== '0' && (
                              <span className="text-xs text-gray-400">Slip: {p.slipNo}</span>
                            )}
                            {p.notes && p.notes !== 'Advance received via API' && (
                              <span className="text-xs text-gray-400 flex items-center gap-0.5 truncate max-w-[140px]">
                                <FileText className="w-3 h-3 shrink-0" />{p.notes}
                              </span>
                            )}
                            {p.notes === 'Advance received via API' && (
                              <span className="text-[10px] bg-blue-50 text-blue-500 px-1.5 py-0.5 rounded font-medium">via API</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3 shrink-0 ml-2">
                        <span className="font-bold text-gray-900 text-sm sm:text-base">{formatCurrency(p.amount)}</span>
                        {!p.isLegacy && (
                          <button onClick={() => setDeleteConfirm(p.id)}
                            className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>{/* /p-4 sm:p-6 */}
        </div>{/* /scrollable body */}
      </div>{/* /card */}
    </div>/* /backdrop */
  );
}
